/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors in the component tree and displays a fallback UI.
 * Prevents the entire app from crashing due to unhandled errors.
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { logger } from '@/utils/logger';
import i18n from '@/lib/i18n';

interface ErrorBoundaryProps {
  /** Child components to wrap */
  children: ReactNode;
  /** Optional fallback component to render on error */
  fallback?: ReactNode;
  /** Called when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary - catches and handles React component errors
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to logging system
    logger.error('ErrorBoundary', 'Uncaught error in component tree', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    this.setState({ errorInfo });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Custom fallback provided
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-paper">
          <div className="max-w-md w-full mx-4 p-8 bg-paper-2 rounded-card ring-1 ring-line text-center">
            {/* Error Icon */}
            <div className="mx-auto w-16 h-16 bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] rounded-full flex items-center justify-center mb-6">
              <AlertTriangle className="w-8 h-8 text-danger" />
            </div>

            {/* Error Message */}
            <h1 className="text-xl font-semibold text-ink mb-2">
              {i18n.t('common.unexpectedErrorTitle')}
            </h1>
            <p className="text-ink-2 mb-6">
              {i18n.t('common.unexpectedErrorBody')}
            </p>

            {/* Error Details (dev only) */}
            {import.meta.env.DEV && error && (
              <div className="mb-6 p-3 bg-paper-2 rounded-control text-left">
                <p className="text-xs font-mono text-danger break-all">
                  {error.message}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-fg rounded-control hover:brightness-[0.92] transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                {i18n.t('common.retry')}
              </button>
              <button
                onClick={this.handleReload}
                className="inline-flex items-center gap-2 px-4 py-2 border border-line text-ink-2 rounded-control hover:bg-paper transition-colors"
              >
                {i18n.t('common.reloadPage')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
